import { useCallback, useEffect } from 'react'

/**
 * Returns a guarded onClose handler that prevents F0Dialog from closing
 * when FolderSelectorF0's dropdown is open.
 *
 * Also overrides `pointer-events: none` on <body> (set by Radix modal dialog)
 * while the dropdown is visible. Without this, the dropdown (portaled to body)
 * inherits pointer-events:none and React's event delegation on #factorialRoot
 * stops working for elements outside the root.
 *
 * FolderSelectorF0 portals its dropdown to document.body (outside the dialog
 * content). Radix's DismissableLayer detects pointerdown outside dialog content
 * and calls onOpenChange(false) → onClose. This guard checks if the dropdown
 * is currently visible and skips the close if so.
 */
export function useFolderSelectorDialogFix(onClose: () => void): () => void {
  // MutationObserver: when .FolderSelectorF0_dropdown appears in DOM,
  // 1. Override body pointer-events so React events work on the dropdown
  // 2. Attach a wheel event listener so scrolling works inside the dropdown
  //    (Radix's react-remove-scroll blocks wheel events on elements outside the dialog)
  useEffect(() => {
    const DROPDOWN_SELECTOR = '.FolderSelectorF0_dropdown'
    let wheelHandler: ((e: Event) => void) | null = null
    let currentDropdown: Element | null = null

    const handleDropdownChange = () => {
      const dropdown = document.querySelector(DROPDOWN_SELECTOR)

      if (dropdown && dropdown !== currentDropdown) {
        document.body.style.pointerEvents = 'auto'

        // Allow scrolling inside the dropdown by stopping wheel event propagation
        // before react-remove-scroll can intercept it
        wheelHandler = (e: Event) => {
          e.stopPropagation()
        }
        dropdown.addEventListener('wheel', wheelHandler, { passive: false })
        currentDropdown = dropdown
      } else if (!dropdown && currentDropdown) {
        document.body.style.pointerEvents = ''
        if (wheelHandler) {
          currentDropdown.removeEventListener('wheel', wheelHandler)
          wheelHandler = null
        }
        currentDropdown = null
      }
    }

    const observer = new MutationObserver(() => {
      handleDropdownChange()
    })

    observer.observe(document.body, { childList: true, subtree: true })

    // Check initial state
    handleDropdownChange()

    return () => {
      observer.disconnect()
      document.body.style.pointerEvents = ''
      if (currentDropdown && wheelHandler) {
        currentDropdown.removeEventListener('wheel', wheelHandler)
      }
    }
  }, [])

  return useCallback(() => {
    const dropdown = document.querySelector('.FolderSelectorF0_dropdown')
    if (dropdown) {
      // Dropdown is open — don't close the dialog
      return
    }
    onClose()
  }, [onClose])
}
